import React, { useEffect, useState } from "react";
import {
  Paper,
  Grid,
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Collapse,
  IconButton,
  Switch,
  InputAdornment,
  Modal,
  Avatar,
  Chip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import Pagination from "@mui/material/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { getCampaign, updateCampaignMasterDataApi } from "../../redux/features/clicksSlice";
import AddRow from "./AddRow";
import UpdateModule from "./UpdateModule";

const CampaignsMaster = () => {
  const dispatch = useDispatch();
  const allCampaignData = useSelector((state) => state.clicks.campaignData);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [searchTerm, setSearchTerm] = useState("");
  const [affActiveFilter, setAffActiveFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const [updateId, setUpdateId] = useState();
  const [campaignMasterUpdate, setCampaignMasterUpdate] = useState([]);
  const [open, setOpen] = useState(false);
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    dispatch(getCampaign())
      .then((data) => {
        if (data.payload?.code == 200) {
          toast.success("Data loaded successfully!", {
            position: "top-center",
            autoClose: 3000,
          });
        }
      })
      .catch((err) => {
        console.log(err, "err fetching campaign");
        toast.error("Something went wrong", {
          position: "top-center",
          autoClose: 3000,
        });
      });
  }, [dispatch]);

  const handleOpen = (campaign) => {
    const rowData = [
      null, // 0
      campaign.id, // 1
      campaign.site_name, // 2
      campaign.host_mid_name, // 3
      campaign.url_match, // 4
      campaign.table_name, // 5
      campaign.cart_url, // 6
      campaign.order_page_url, // 7
      campaign.fb_pixel_id, // 8
      campaign.fb_pixel_pages, // 9
      campaign.blocked_pages, // 10
      campaign.session_wt_min, // 11
      campaign.live_on, // 12
      campaign.cashback_aff, // 13
      campaign.normal_page_per_k, // 14
      campaign.cart_page_per, // 15
      campaign.min_no_visits, // 16
      campaign.country, // 17
      campaign.country_except, // 18
      campaign.device, // 19
      campaign.black_list_aff_wait_min, // 20
      campaign.our_aff_wait_min, // 21
      campaign.aff_active, // 22
      campaign.retargetting_active, // 23
      campaign.data_act, // 24
      campaign.aff_wait_hr, // 25
    ];
    setOpen(true);
    setUpdateId(campaign.id);
    setCampaignMasterUpdate(rowData);
  };

  const handleClose = () => setOpen(false);

  const handleExpandClick = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleAffActive = (campaign) => {
    const newValue = campaign.aff_active == 1 ? 0 : 1;
    const formData = { id: campaign.id, aff_active: newValue };
    dispatch(updateCampaignMasterDataApi(formData))
      .then(() => {
        dispatch(getCampaign());
        toast.success("Status Updated Successfully!");
      })
      .catch(() => {
        toast.error("Failed to update status");
      });
  };

  const filteredData = (allCampaignData || []).filter((campaign) => {
    const matchesSearch =
      campaign.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.host_mid_name?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesAffActive = true;
    if (affActiveFilter === "active") matchesAffActive = campaign.aff_active == 1;
    else if (affActiveFilter === "inactive") matchesAffActive = campaign.aff_active == 0;

    return matchesSearch && matchesAffActive;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const totalCampaigns = allCampaignData?.length || 0;
  const activeCampaigns = allCampaignData?.filter((c) => c.aff_active == 1)?.length || 0;

  return (
    <Box sx={{ padding: { xs: "8px", sm: "12px" }, minHeight: "100vh", backgroundColor: "#f4f7f9" }}>
      <Helmet>
        <title>Alpha Targeting || Campaign</title>
      </Helmet>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          Campaigns Master
        </Typography>
        <Box>
          <Chip label={`Total: ${totalCampaigns}`} color="primary" variant="outlined" sx={{ mr: 1, fontSize: '1rem', fontWeight: 'bold' }} />
          <Chip label={`Active: ${activeCampaigns}`} color="success" variant="outlined" sx={{ fontSize: '1rem', fontWeight: 'bold' }} />
        </Box>
      </Box>

      {showInput && <AddRow setShowInput={setShowInput} />}

      <Paper sx={{ padding: "12px", marginBottom: "16px", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Search Site Name"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Affiliate Status</InputLabel>
              <Select
                value={affActiveFilter}
                onChange={(e) => {
                  setAffActiveFilter(e.target.value);
                  setPage(1);
                }}
                label="Affiliate Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} sx={{ textAlign: "right" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setShowInput((prev) => !prev)}
            >
              {showInput ? "Cancel" : "Add Campaign"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={1.5}>
        {currentData.map((campaign) => (
          <Grid item xs={12} sm={6} lg={4} key={campaign.id}>
            <Card 
              variant="outlined" 
              sx={{ 
                borderRadius: "12px", 
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                transition: "0.3s",
                "&:hover": {
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)"
                }
              }}
            >
              <Box sx={{ padding: "10px 14px", backgroundColor: "#ffffff", borderBottom: "1px solid #f0f0f0" }}>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item xs={8} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: campaign.aff_active == 1 ? "#4caf50" : "#f44336", width: 32, height: 32, fontSize: "1rem" }}>
                      {campaign.site_name ? campaign.site_name.charAt(0).toUpperCase() : "N"}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap title={campaign.site_name}>
                        {campaign.site_name || "N/A"}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={campaign.aff_active == 1 ? "Active" : "Inactive"} 
                        color={campaign.aff_active == 1 ? "success" : "error"} 
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem", mt: 0.5 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    <Switch
                      checked={campaign.aff_active == 1}
                      onChange={() => handleToggleAffActive(campaign)}
                      color="primary"
                      size="small"
                    />
                    <IconButton size="small" onClick={() => handleOpen(campaign)} sx={{ ml: 1, bgcolor: "#f0f4ff", "&:hover": { bgcolor: "#e0e8ff" } }}>
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>

              <CardContent sx={{ padding: "10px 14px", "&:last-child": { paddingBottom: "10px" } }}>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="textSecondary">Normal Page:</Typography>
                    <Typography variant="body1">{(campaign.normal_page_per_k / 10)}%</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="textSecondary">Cart Page:</Typography>
                    <Typography variant="body1">{campaign.cart_page_per}%</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="textSecondary">Country:</Typography>
                    <Typography variant="body1" noWrap title={campaign.country}>{campaign.country}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="textSecondary">Device:</Typography>
                    <Typography variant="body1" noWrap title={campaign.device}>{campaign.device}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="textSecondary">Our Aff Wt:</Typography>
                    <Typography variant="body1">{campaign.our_aff_wait_min}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="textSecondary">Aff Wt Hr:</Typography>
                    <Typography variant="body1">{campaign.aff_wait_hr}</Typography>
                  </Grid>
                </Grid>
              </CardContent>

              <CardActions disableSpacing sx={{ justifyContent: "space-between", px: 1.5, pb: 1, pt: 0 }}>
                <Button
                  size="small"
                  onClick={() => window.open(`/camplinks?site=${encodeURIComponent(campaign.site_name)}&store_id=${campaign.id}`, "_blank")}
                  variant="outlined"
                  color="primary"
                  sx={{ borderRadius: "20px", textTransform: "none" }}
                >
                  Camp Links
                </Button>
                <Button
                  size="small"
                  onClick={() => handleExpandClick(campaign.id)}
                  endIcon={expanded[campaign.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: "none", color: "text.secondary" }}
                >
                  {expanded[campaign.id] ? "Hide" : "Details"}
                </Button>
              </CardActions>

              <Collapse in={expanded[campaign.id]} timeout="auto" unmountOnExit>
                <CardContent sx={{ borderTop: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">ID:</Typography>
                      <Typography variant="body1">{campaign.id}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Host Mid Name:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.host_mid_name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">URL Match:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.url_match}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Table Name:</Typography>
                      <Typography variant="body1">{campaign.table_name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Cart URL:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.cart_url}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Order Page URL:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.order_page_url}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">FB Pixel ID:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.fb_pixel_id}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">FB Pixel Pages:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.fb_pixel_pages}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Blocked Pages:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.blocked_pages}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Cashback Aff:</Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>{campaign.cashback_aff}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Min No Visits:</Typography>
                      <Typography variant="body1">{campaign.min_no_visits}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Country Except:</Typography>
                      <Typography variant="body1">{campaign.country_except}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Blacklist Aff Wt Min:</Typography>
                      <Typography variant="body1">{campaign.black_list_aff_wait_min}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Retargetting Active:</Typography>
                      <Typography variant="body1">{campaign.retargetting_active}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Data Act:</Typography>
                      <Typography variant="body1">{campaign.data_act}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Session Wt Min:</Typography>
                      <Typography variant="body1">{campaign.session_wt_min}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Live On:</Typography>
                      <Typography variant="body1">{campaign.live_on}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Collapse>
            </Card>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "30px" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <UpdateModule
          handleClose={handleClose}
          updateId={updateId}
          campaignMasterUpdate={campaignMasterUpdate}
        />
      </Modal>
    </Box>
  );
};

export default CampaignsMaster;
